import React, { FC } from 'react';
import Select from 'react-select';
import { useController, UseControllerProps, useFormContext } from 'react-hook-form';
import { handleMonthInput, MonthPicker } from '@common/datePicker';
import ErrorMessage from '@components/profile/profileOverlay/common/ErrorMessage';
import { ReactSelectStyles } from '@components/profile/profileOverlay/overlays/ReactSelectStyles';
import styles from '../../militaryPreference.module.scss';
import {
  militaryClassOptions,
  militaryDischargeOptions,
  militaryOptions,
  MilitaryStatus,
  militaryStatusOptions,
} from '@domain/constant/profile/PreferentialType';

import DateFormat from '@utils/date/format';
import classnames from 'classnames/bind';
const cx = classnames.bind(styles);

type IMilitaryPeriodProps = Omit<UseControllerProps, 'name'> & {
  prefixName: string;
};

const Military: FC<IMilitaryPeriodProps> = ({ prefixName }) => {
  const { control } = useFormContext();

  const statusName = `${prefixName}.status`;
  const {
    field: statusField,
    fieldState: statusMeta,
    formState: { errors: statusErrors },
  } = useController({
    name: statusName,
    control,
  });

  const typeName = `${prefixName}.type`;
  const {
    field: typeField,
    fieldState: typeMeta,
    formState: { errors: typeErrors },
  } = useController({
    name: typeName,
    control,
  });

  const militaryClassName = `${prefixName}.militaryClass`;
  const {
    field: militaryClassField,
    fieldState: militaryClassMeta,
    formState: { errors: militaryClassErrors },
  } = useController({
    name: militaryClassName,
    control,
  });

  const dischargeTypeName = `${prefixName}.dischargeType`;
  const {
    field: dischargeTypeField,
    fieldState: dischargeTypeMeta,
    formState: { errors: dischargeTypeErrors },
  } = useController({
    name: dischargeTypeName,
    control,
  });

  const startDateName = `${prefixName}.startDate`;
  const {
    field: startDateField,
    fieldState: startDateMeta,
    formState: { errors: startDateErrors },
  } = useController({
    control,
    name: startDateName,
  });
  const selectedStartDate = startDateField.value ? new Date(startDateField.value) : null;
  const handleChangeStartDate = (date: Date | null) => {
    if (!date) return;
    startDateField.onChange(DateFormat.format(date, 'yyyy-MM'));
  };

  const endDateName = `${prefixName}.endDate`;
  const {
    field: endDateField,
    fieldState: endDateMata,
    formState: { errors: endDateErrors },
  } = useController({
    control,
    name: endDateName,
  });
  const selectedEndDate = endDateField.value ? new Date(endDateField.value) : null;
  const handleChangeEndDate = (date: Date | null) => {
    if (!date) return;
    endDateField.onChange(DateFormat.format(date, 'yyyy-MM'));
  };

  const onChangeStatus = (v: any) => {
    statusField.onChange(v);
    if (v === null) return;
    switch (v?.value) {
      case MilitaryStatus.DISCHARGE:
        return;
      case MilitaryStatus.IN_SERVICE:
        endDateField.onChange('');
        dischargeTypeField.onChange(null);
        return;
      default:
        typeField.onChange(null);
        militaryClassField.onChange(null);
        dischargeTypeField.onChange(null);
        startDateField.onChange('');
        endDateField.onChange('');
        return;
    }
  };

  //병역 구분 선택안하거나 비대상자, 미필인 경우 전체 isDisable
  const isDisabledAll =
    !statusField.value?.value ||
    [MilitaryStatus.NONE, MilitaryStatus.EXEMPTION, MilitaryStatus.NON_DISCHARGE].includes(statusField.value?.value);

  const isDisabledType = isDisabledAll; //군별
  const isDisabledMilitaryClass = isDisabledAll; //계급
  const isDisabledDischargeType = isDisabledAll || [MilitaryStatus.IN_SERVICE].includes(statusField.value?.value); //제대구분
  const isDisabledStartDate = isDisabledAll; //복무 시작일
  const isDisabledEndDate = isDisabledAll || [MilitaryStatus.IN_SERVICE].includes(statusField.value?.value); //복무 종료일

  return (
    <>
      <div className={cx('infoInner')}>
        <em className={cx('infoTitle')}>병역 사항</em>
        <div className={cx('selectArea')}>
          <div className={cx('selectWrap')}>
            <Select
              {...statusField}
              onChange={onChangeStatus}
              className={cx('select', { error: statusMeta.invalid })}
              placeholder={'선택'}
              styles={ReactSelectStyles}
              isSearchable={false}
              options={militaryStatusOptions}
            />
            <ErrorMessage name={statusName} errors={statusErrors} />
          </div>
          <div className={cx('selectWrap')}>
            <Select
              {...typeField}
              className={cx('select', { error: typeMeta.invalid })}
              placeholder={'군별'}
              styles={ReactSelectStyles}
              isSearchable={false}
              options={militaryOptions}
              isDisabled={isDisabledType}
            />
            <ErrorMessage name={typeName} errors={typeErrors} />
          </div>
          <div className={cx('selectWrap')}>
            <Select
              {...militaryClassField}
              className={cx('select', { error: militaryClassMeta.invalid })}
              placeholder={'계급'}
              styles={ReactSelectStyles}
              isSearchable={false}
              options={militaryClassOptions}
              isDisabled={isDisabledMilitaryClass}
            />
            <ErrorMessage name={militaryClassName} errors={militaryClassErrors} />
          </div>
          <div className={cx('selectWrap', 'type2')}>
            <Select
              {...dischargeTypeField}
              className={cx('select', { error: dischargeTypeMeta.invalid })}
              placeholder={'제대 구분'}
              styles={ReactSelectStyles}
              isSearchable={false}
              options={militaryDischargeOptions}
              isDisabled={isDisabledDischargeType}
            />
            <ErrorMessage name={dischargeTypeName} errors={dischargeTypeErrors} />
          </div>
        </div>
      </div>
      <div className={cx('infoInner')}>
        <em className={cx('infoTitle')}>병역 기간</em>
        <div className={cx('periodArea')}>
          <div className={cx('periodWrap')}>
            <MonthPicker
              selected={selectedStartDate}
              onChange={handleChangeStartDate}
              onChangeRaw={handleMonthInput}
              className={cx('periodInput', { error: startDateMeta.invalid }, { complete: !!startDateField.value })}
              placeholderText="연도. 월"
              maxDate={new Date('9999-12')}
              disabled={isDisabledStartDate}
            />
            <ErrorMessage name={startDateName} errors={startDateErrors} />
          </div>
          <mark>~</mark>
          <div className={cx('periodWrap')}>
            <MonthPicker
              selected={selectedEndDate}
              onChange={handleChangeEndDate}
              onChangeRaw={handleMonthInput}
              className={cx('periodInput', { error: endDateMata.invalid }, { complete: !!endDateField.value })}
              placeholderText="연도. 월"
              maxDate={new Date('9999-12')}
              disabled={isDisabledEndDate}
            />
            <ErrorMessage name={endDateName} errors={endDateErrors} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Military;
